import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { ProductEventDataList } from 'corgi-types';
import {
  background,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  boxShadow,
  position,
  width,
  zIndex,
  type BackgroundProps,
  type BorderColorProps,
  type BorderRadiusProps,
  type BorderStyleProps,
  type BorderWidthProps,
  type BoxShadowProps,
  type PositionProps,
  type WidthProps,
  type ZIndexProps,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import { getForwardPropOpts } from 'common/theme/helpers/forward-props';
import type { PointerEventProps } from 'common/theme/system-utilities';
import { pointerEvents } from 'common/theme/system-utilities';
import type { ImageMarkerProps } from '../types';
import { HorizontalCard } from './HorizontalCard';

const Tooltip = styled(Flex, getForwardPropOpts(['visible']))<
  PositionProps &
    BackgroundProps &
    BorderRadiusProps &
    BoxShadowProps &
    ZIndexProps &
    PointerEventProps & { visible: boolean }
>`
  ${position};
  ${background};
  ${borderRadius};
  ${boxShadow};
  ${pointerEvents};
  ${zIndex};
`;

const SLIDE_WIDTH = [225, 325, null, 382];

const SlideContainer = styled.section<
  WidthProps & BorderRadiusProps & BorderWidthProps & BorderColorProps & BorderStyleProps
>`
  ${width};
  ${borderRadius};
  ${borderWidth};
  ${borderColor};
  ${borderStyle}
`;

const slideContainerStyles = {
  borderWidth: 'small',
  borderRadius: 'medium',
  borderStyle: 'solid',
};

const getTooltipTransform = (x: number): string => {
  // If dot is on the right side, align tooltip to the right
  if (x > 0.7) {
    return 'translate(-100%, -100%)';
  }
  // If dot is on the left side, align tooltip to the left
  if (x < 0.3) {
    return 'translate(0%, -100%)';
  }

  return 'translate(-50%, -100%)';
};

const tooltipStyles: Partial<ComponentProps<typeof Tooltip>> = {
  position: 'absolute',
  background: 'white',
  borderRadius: 'medium',
  boxShadow: 'low',
  zIndex: 2,
};

interface DesktopTooltipProps {
  activeMarker: ImageMarkerProps;
  activeMarkerIndex: number;
  list?: ProductEventDataList;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  id?: string;
  linkRef?: React.RefObject<HTMLAnchorElement>;
  onEscape: () => void;
}

export const DesktopTooltip = ({
  activeMarker,
  activeMarkerIndex,
  list,
  onMouseEnter,
  onMouseLeave,
  id,
  linkRef,
  onEscape,
}: DesktopTooltipProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onEscape();
    }
  };

  return (
    <Tooltip
      visible={!!activeMarker}
      {...tooltipStyles}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeyDown}
      role="tooltip"
      id={id}
      style={{
        transform: getTooltipTransform(activeMarker.x),
        top: `${activeMarker.y * 100}%`,
        left: `${activeMarker.x * 100}%`,
      }}
    >
      <SlideContainer width={SLIDE_WIDTH} {...slideContainerStyles}>
        <HorizontalCard
          article={activeMarker.article}
          list={list}
          index={activeMarkerIndex}
          linkRef={linkRef}
          onEscape={onEscape}
        />
      </SlideContainer>
    </Tooltip>
  );
};
